import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { passwordResetService } from '../../services/passwordResetService';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'react-hot-toast';
import {
  Eye,
  EyeOff,
  Lock,
  Copy,
  ArrowLeft,
  CheckCircle,
  Shield,
  RefreshCw,
  AlertCircle,
  Check
} from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    let isMounted = true; // Flag to track component mount status

    const validateToken = async () => {
      if (!token) {
        toast.error('Geçersiz veya eksik token');
        if (isMounted) {
             navigate('/forgot-password');
        }
        return;
      }

      // Only set validating state if the component is still mounted
      if (isMounted) {
          setIsValidating(true);
      }
      
      try {
        await passwordResetService.verifyResetToken(token);
        if (isMounted) {
            setIsTokenValid(true);
        }
      } catch (error) {
        toast.error(error.message || 'Token geçersiz veya süresi dolmuş');
        if (isMounted) {
             navigate('/forgot-password');
        }
      } finally {
        if (isMounted) {
             setIsValidating(false);
        }
      }
    };

    validateToken();

    // Cleanup function to set isMounted to false when the component unmounts
    return () => {
        isMounted = false;
    };
  }, [token, navigate]);

  // Şifre gereksinimlerini kontrol eden fonksiyon
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push('En az 8 karakter');
    if (!hasUpperCase) errors.push('En az 1 büyük harf');
    if (!hasLowerCase) errors.push('En az 1 küçük harf');
    if (!hasNumbers) errors.push('En az 1 rakam');
    if (!hasSpecialChar) errors.push('En az 1 özel karakter');

    return errors;
  };

  // Şifreyi panoya kopyalamak için
  const handleCopy = (value) => {
    navigator.clipboard.writeText(value);
    toast.success('Şifre kopyalandı!');
  };

  // Güvenli şifre oluşturma fonksiyonu
  const generateSecurePassword = () => {
    const length = 16;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    let chars = uppercase + lowercase + numbers + symbols;
    let password = '';

    // En az bir karakter her kategoriden
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Geri kalanı random
    for (let i = 4; i < length; i++) {
      password += chars[Math.floor(Math.random() * chars.length)];
    }

    // Karakterleri karıştır
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setGeneratedPassword(password);
    setPasswordStrength(5); // Maksimum güç
  };

  // Şifreyi kullan
  const useGeneratedPassword = () => {
    setFormData(prev => ({
      ...prev,
      password: generatedPassword,
      confirmPassword: generatedPassword
    }));
    setShowGenerateModal(false);
    toast.success('Güvenli şifre uygulandı!');
  };

  // Şifreyi kopyala
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      toast.error('Şifre kopyalanamadı');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      toast.error(
        <div>
          <p>Şifre gereksinimleri:</p>
          <ul className="list-disc pl-4 mt-1">
            {passwordErrors.map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      );
      return;
    }

    setIsLoading(true);
    try {
      await passwordResetService.resetPassword(token, formData.password);
      toast.success('Şifreniz başarıyla güncellendi');
      navigate('/signin'); // veya '/login' rotası
    } catch (error) {
      toast.error(error.message || 'Şifre güncellenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Token doğrulanırken gösterilecek loading spinner
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Token geçersizse null döndürüyoruz (toast zaten verildi ve yönlendirildi)
  if (!isTokenValid) {
    return null;
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-blue-50 via-white to-gray-50'
      } py-12 px-4 sm:px-6 lg:px-8`}
    >
      {/* Arka plan grid deseni */}
      <div className="absolute inset-0">
        <div
          className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] ${
            isDark ? 'opacity-10' : 'opacity-20'
          }`}
        />
      </div>

      {/* Ana kart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div
          className={`${
            isDark
              ? 'bg-white/10 backdrop-blur-xl border-white/10'
              : 'bg-white/80 backdrop-blur-xl border-gray-200'
          } p-8 rounded-3xl border shadow-2xl`}
        >
          {/* Geri dön butonu */}
          <button
            onClick={() => navigate('/signin')}
            className={`group mb-8 flex items-center text-sm font-medium ${
              isDark
                ? 'text-gray-300 hover:text-white'
                : 'text-gray-600 hover:text-gray-900'
            } transition-colors`}
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Giriş sayfasına dön
          </button>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mb-8"
          >
            <h2
              className={`text-3xl font-bold ${
                isDark ? 'text-white' : 'text-gray-900'
              } mb-2`}
            >
              Yeni Şifre Belirleme
            </h2>
            <p
              className={`${
                isDark ? 'text-gray-300' : 'text-gray-600'
              } mb-2`}
            >
              Lütfen yeni şifrenizi belirleyin.
            </p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {/* Güvenli Şifre Oluştur Butonu */}
            <motion.button
              type="button"
              onClick={() => {
                setShowGenerateModal(true);
                generateSecurePassword();
              }}
              className={`w-full flex items-center justify-center space-x-2 py-3 px-4 rounded-xl ${
                isDark 
                  ? 'bg-white/5 hover:bg-white/10 text-white' 
                  : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
              } transition-all duration-200`}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <Shield className="w-5 h-5" />
              <span>Güvenli Şifre Oluştur</span>
            </motion.button>

            {/* Şifre Alanı */}
            <div>
              <label
                className={`block text-sm font-medium ${
                  isDark ? 'text-gray-200' : 'text-gray-700'
                } mb-2`}
              >
                Yeni Şifre
              </label>
              <div className="relative">
                <Lock
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className={`w-full pl-12 pr-12 py-3 ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                  } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                  placeholder="Yeni şifreniz"
                  required
                />
                {/* Göz ikonu */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-10 pr-2 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff
                      className={`w-5 h-5 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                  ) : (
                    <Eye
                      className={`w-5 h-5 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                  )}
                </button>
                {/* Kopyala ikonu */}
                <button
                  type="button"
                  onClick={() => handleCopy(formData.password)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <Copy
                    className={`w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    } hover:text-gray-600 transition-colors`}
                  />
                </button>
              </div>
            </div>

            {/* Şifre Tekrar */}
            <div>
              <label
                className={`block text-sm font-medium ${
                  isDark ? 'text-gray-200' : 'text-gray-700'
                } mb-2`}
              >
                Yeni Şifre (Tekrar)
              </label>
              <div className="relative">
                <Lock
                  className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}
                />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className={`w-full pl-12 pr-12 py-3 ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder-gray-400'
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                  } border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                  placeholder="Şifrenizi tekrar girin"
                  required
                />
                {/* Göz ikonu */}
                <button
                  type="button"
                  onClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  className="absolute inset-y-0 right-10 pr-2 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOff
                      className={`w-5 h-5 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                  ) : (
                    <Eye
                      className={`w-5 h-5 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    />
                  )}
                </button>
                {/* Kopyala ikonu */}
                <button
                  type="button"
                  onClick={() => handleCopy(formData.confirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <Copy
                    className={`w-5 h-5 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    } hover:text-gray-600 transition-colors`}
                  />
                </button>
              </div>
            </div>

            {/* Form butonu */}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type="submit"
              disabled={isLoading}
              className={`group relative w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium shadow-lg transition-all duration-200`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 
                      0 0 5.373 0 12h4zm2 5.291A7.962 
                      7.962 0 014 12H0c0 3.042 1.135 
                      5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Şifre Güncelleniyor...
                </div>
              ) : (
                'Şifreyi Güncelle'
              )}
            </motion.button>
          </motion.form>
        </div>
      </motion.div>

      {/* Güvenli Şifre Oluşturma Modal */}
      {showGenerateModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowGenerateModal(false)}
          />
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`relative w-full max-w-md p-6 rounded-2xl shadow-xl ${
              isDark ? 'bg-gray-800' : 'bg-white'
            }`}
          >
            <div className="mb-4">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Güvenli Şifre Oluşturucu
              </h3>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Benzersiz ve güçlü bir şifre oluşturuldu
              </p>
            </div>

            <div className={`relative mt-4 p-4 rounded-lg ${
              isDark ? 'bg-gray-700' : 'bg-gray-50'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`font-mono text-lg ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {generatedPassword}
                </span>
                <div className="flex space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => generateSecurePassword()}
                    className={`p-2 rounded-lg ${
                      isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RefreshCw className="w-5 h-5" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => copyToClipboard(generatedPassword)}
                    className={`p-2 rounded-lg ${
                      isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                    } transition-colors`}
                  >
                    {copySuccess ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                  </motion.button>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center space-x-2">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 0.5 }}
                    className="h-full bg-green-500"
                  />
                </div>
                <span className={`text-sm font-medium ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Çok Güçlü
                </span>
              </div>

              <div className="flex items-start space-x-2 text-sm">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  Bu şifre büyük/küçük harfler, sayılar ve özel karakterler içerir
                </span>
              </div>
            </div>

            <div className="mt-6 flex space-x-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={useGeneratedPassword}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Bu Şifreyi Kullan
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowGenerateModal(false)}
                className={`px-4 py-2 rounded-lg ${
                  isDark 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                İptal
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ResetPassword;
